<template>
  <section v-if="bundle" class="mt-3">
    <div class="d-flex align-center flex-wrap">
      <span class="font-weight-bold mr-2">Estado de la solicitud:</span>
      <v-chip small :color="requestStatus.color" class="text-white">
        {{ requestStatus.name }}
      </v-chip>
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="18"
        width="2"
        class="ml-2"
      ></v-progress-circular>
      <v-chip
        v-if="editableUntil"
        small
        class="ml-2 text-white"
        color="orange darken-3"
      >
        Editable hasta el {{ formatEditableUntilCl(editableUntil) }} inclusive
      </v-chip>
      <div v-if="isPostedToSap" class="ml-auto d-flex flex-wrap" style="gap: 8px">
        <v-btn
          x-small
          outlined
          color="blue darken-2"
          :disabled="!bundleStatus"
          @click="downloadSummary"
        >
          Descargar resumen
        </v-btn>
        <v-btn x-small text color="blue darken-2" @click="dialogHistory = true">
          Historial de cambios
        </v-btn>
      </div>
    </div>
    <div v-if="bundle.delivery_info" class="text-caption mt-1">
      Despacho: {{ bundle.delivery_info.address }}, {{ bundle.delivery_info.commune }},
      {{ bundle.delivery_info.region }}
      <span v-if="bundle.delivery_info.contact"> · Contacto: {{ bundle.delivery_info.contact }}</span>
    </div>
    <v-alert
      v-if="lateLines.length"
      type="error"
      text
      dense
      border="left"
      class="mt-3 mb-0"
    >
      <b>Riesgo de despacho:</b>
      {{ lateLines.length }} {{ lateLines.length === 1 ? "línea de compra llega" : "líneas de compra llegan" }}
      después del {{ bundle.desired_dispatch_date.split("-").reverse().join("/") }}
      ({{ lateLines.map((line) => `${line.code}: ${line.arrival.label}`).join(", ") }}).
    </v-alert>
    <v-dialog v-model="dialogHistory" max-width="900px" light>
      <v-card light>
        <v-card-title>
          <span class="text-h6">Historial de cambios {{ getSliCode(bundle) }}</span>
          <v-spacer></v-spacer>
          <v-btn icon aria-label="Cerrar" @click="dialogHistory = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p v-if="!edits.length" class="mb-0">La solicitud no ha tenido cambios desde que se envió.</p>
          <v-simple-table v-else dense light class="request-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Usuario</th>
                <th>Estado</th>
                <th>Cambios</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="edit in edits" :key="edit.id">
                <td>{{ edit.created_at ? moment(edit.created_at).format("DD/MM/YYYY HH:mm") : "-" }}</td>
                <td>{{ edit.created_by }}</td>
                <td>
                  <v-chip x-small class="text-white" :color="getEditStatus(edit).color">
                    {{ getEditStatus(edit).name }}
                  </v-chip>
                  <div v-if="edit.result && edit.result.error" class="red--text text-caption">
                    {{ edit.result.error }}
                  </div>
                </td>
                <td>
                  <div v-for="change in edit.changes || []" :key="change.code" class="text-caption">
                    {{ change.code }}: {{ change.transfer_before }} → {{ change.transfer_after }}
                    <span v-if="change.purchase_after > change.purchase_before">
                      (compra +{{ change.purchase_after - change.purchase_before }})
                    </span>
                  </div>
                </td>
                <td>{{ edit.comment || "-" }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <template v-if="isPostedToSap && bundleStatus">
      <p
        v-if="!bundleStatus.transfers.length && !bundleStatus.purchases.length"
        class="mt-3 mb-0"
      >
        <template v-if="resyncing">Sincronizando los documentos de SAP con Entregas…</template>
        <template v-else>
          Los documentos de SAP aún no se sincronizan con Entregas. Vuelve a
          actualizar el estado en unos minutos.
        </template>
      </p>
      <v-expansion-panels v-else multiple flat light class="mt-3 request-documents">
        <v-expansion-panel
          v-for="transfer in bundleStatus.transfers"
          :key="`st-${transfer.sap_code}`"
        >
          <v-expansion-panel-header>
            <div class="d-flex align-center flex-wrap">
              <b class="mr-2"
                >Solicitud de traslado (ST) N° {{ transfer.sap_code }}</b
              >
              <v-chip
                small
                class="text-white"
                :color="transfer.promotion_status === 'promoted' ? 'green darken-2' : 'blue darken-2'"
              >
                {{ getTransferStatusName(transfer) }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table dense light class="request-table">
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th class="text-center">Estándar</th>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th class="text-right">Cantidad reservada</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in transfer.items" :key="line.code">
                  <td>
                    {{
                      materialInfo(line.code)
                        ? materialInfo(line.code).isMainEquipment
                          ? "Equipo principal"
                          : "Material"
                        : "-"
                    }}
                  </td>
                  <td class="text-center">
                    <template v-if="materialInfo(line.code)">
                      <v-icon v-if="materialInfo(line.code).isStandard" small color="primary">
                        mdi-check-circle-outline
                      </v-icon>
                      <v-tooltip v-else top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon small color="error" v-bind="attrs" v-on="on">mdi-alert-outline</v-icon>
                        </template>
                        <span>Producto no estándar</span>
                      </v-tooltip>
                    </template>
                    <span v-else>-</span>
                  </td>
                  <td>{{ line.code }}</td>
                  <td>{{ lineDescription(line) }}</td>
                  <td class="text-right">{{ line.quantity }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-for="purchase in bundleStatus.purchases"
          :key="`sc-${purchase.sap_code}`"
        >
          <v-expansion-panel-header>
            <div class="d-flex align-center flex-wrap">
              <b class="mr-2"
                >Solicitud de compra (SC) N° {{ purchase.sap_code }}</b
              >
              <span
                >{{ getReceivedLinesCount(purchase) }} de
                {{ purchase.items.length }} líneas recibidas</span
              >
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table dense light class="request-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th class="text-right">Solicitado</th>
                  <th class="text-right">Recibido</th>
                  <th>Orden de compra</th>
                  <th>Llegada estimada</th>
                  <th>Estado</th>
                  <th>Factura</th>
                  <th>Pago</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in purchase.items" :key="line.code">
                  <td>{{ line.code }}</td>
                  <td>{{ lineDescription(line) }}</td>
                  <td class="text-right">{{ line.quantity }}</td>
                  <td class="text-right">{{ line.received_quantity }}</td>
                  <td>
                    {{ line.purchase_orders.length ? line.purchase_orders.join(", ") : "-" }}
                  </td>
                  <td>
                    <template v-if="getArrival(line)">
                      <span
                        class="arrival-dot"
                        :class="getArrival(line).color"
                        aria-hidden="true"
                      ></span>
                      {{ getArrival(line).label }}
                      <span v-if="getArrival(line).source" class="text-caption">
                        ({{ getArrival(line).source }})
                      </span>
                    </template>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <v-chip
                      x-small
                      class="text-white"
                      :color="getPurchaseLineStatus(line).color"
                    >
                      {{ getPurchaseLineStatus(line).name }}
                    </v-chip>
                  </td>
                  <template v-if="!invoices">
                    <td colspan="2" class="text-caption grey--text">
                      {{ loadingInvoices ? "Consultando SAP…" : "No se pudo consultar" }}
                    </td>
                  </template>
                  <template v-else>
                    <td>
                      <div
                        v-for="invoice in getLineInvoices(purchase, line).invoices"
                        :key="invoice.doc_num"
                        class="text-caption"
                      >
                        N° {{ invoice.folio || invoice.doc_num }}
                        <span v-if="invoice.due_date">
                          (vence {{ moment(invoice.due_date).format("DD/MM/YYYY") }})
                        </span>
                      </div>
                      <span v-if="!getLineInvoices(purchase, line).invoices.length">-</span>
                    </td>
                    <td>
                      <v-chip
                        x-small
                        class="text-white"
                        :color="getPaymentStatus(getLineInvoices(purchase, line).payment_status).color"
                      >
                        {{ getPaymentStatus(getLineInvoices(purchase, line).payment_status).name }}
                      </v-chip>
                    </td>
                  </template>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </section>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { CUBAGE_BUNDLE_STATUS } from "@/constants/cubage";
import {
  getRequestStatus,
  getPurchaseLineStatus,
  getPaymentStatus,
  formatEditableUntilCl,
  formatPickingDayCl,
  getPurchaseLineArrival,
  getSliCode,
} from "@/helpers/cubage";
import { utils, writeFileXLSX } from "xlsx";

// Status of each change made to a sent request (transfer_bundle_edits)
const EDIT_STATUS = {
  applying: { name: "Aplicando", color: "blue darken-2" },
  applied: { name: "Aplicada", color: "green darken-2" },
  partially_applied: { name: "Aplicada parcialmente", color: "red darken-2" },
  failed: { name: "Fallida", color: "red darken-2" },
};

// Consolidated status of a cubicador v2 request: one status for the whole request, plus its
// single ST and SC (expandable to their lines) instead of the SAP code repeated per material.
export default {
  components: {
  },
  props: {
    bundle: {
      type: Object,
      default: null,
    },
    // code -> { isMainEquipment, isStandard }, from the cubicación's materials
    materialsInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      moment,
      loading: false,
      bundleStatus: null,
      dialogHistory: false,
      resyncing: false,
      // Bundle already re-synced in this view, so it's only tried once
      resyncedBundleId: null,
      // Supplier invoices/payments (SAP, live): only loaded when asked for
      invoices: null,
      loadingInvoices: false,
      // code -> SAP lead time (days), for purchase lines without a PO date
      leadTimes: {},
    };
  },
  methods: {
    ...mapActions({
      getBundleStatus: "promotion/getBundleStatus",
      resyncBundleEntregas: "cubage/resyncBundleEntregas",
      getBundleInvoices: "promotion/getBundleInvoices",
      getMaterialsLeadTime: "cubage/getMaterialsLeadTime",
    }),
    formatEditableUntilCl,
    formatPickingDayCl,
    materialInfo(code) {
      return this.materialsInfo[code] || null;
    },
    getArrival(line) {
      return getPurchaseLineArrival(line, this.leadTimes[line.code], this.bundle.desired_dispatch_date);
    },
    async loadLeadTimes() {
      const codes = [];
      (this.bundleStatus ? this.bundleStatus.purchases : []).forEach((purchase) =>
        purchase.items.forEach((line) => {
          if (!line.delivery_date && !line.cancelled && line.received_quantity < line.quantity) {
            codes.push(line.code);
          }
        })
      );
      this.leadTimes = codes.length ? (await this.getMaterialsLeadTime({ codes })) || {} : {};
    },
    downloadSummary() {
      const transferRows = [];
      this.bundleStatus.transfers.forEach((transfer) =>
        transfer.items.forEach((line) => {
          const info = this.materialInfo(line.code);
          transferRows.push({
            "ST": transfer.sap_code,
            "Categoría": info ? (info.isMainEquipment ? "Equipo principal" : "Material") : "",
            "Tipo": info ? (info.isStandard ? "Estándar" : "No estándar") : "",
            "Código": line.code,
            "Descripción": line.description,
            "Cantidad": line.quantity,
            "Estado": this.getTransferStatusName(transfer),
          });
        })
      );
      const purchaseRows = [];
      this.bundleStatus.purchases.forEach((purchase) =>
        purchase.items.forEach((line) => {
          const arrival = this.getArrival(line);
          purchaseRows.push({
            "SC": purchase.sap_code,
            "Código": line.code,
            "Descripción": line.description,
            "Solicitado": line.quantity,
            "Recibido": line.received_quantity,
            "Orden de compra": line.purchase_orders.join(", "),
            "Llegada estimada": arrival ? arrival.label : "Anulada",
            "Estado": getPurchaseLineStatus(line).name,
          });
        })
      );
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, utils.json_to_sheet(transferRows), "Traslado (ST)");
      utils.book_append_sheet(workbook, utils.json_to_sheet(purchaseRows), "Compra (SC)");
      writeFileXLSX(workbook, `Resumen_${getSliCode(this.bundle)}.xlsx`);
    },
    getPurchaseLineStatus,
    getPaymentStatus,
    lineDescription(line) {
      // entrega keeps material names in another field, so the cubicación's own name comes first
      const info = this.materialInfo(line.code);
      return (info && info.description) || line.description || "-";
    },
    async loadInvoices(silent = false) {
      this.loadingInvoices = true;
      const resp = await this.getBundleInvoices({ bundle_id: this.bundle.id });
      this.loadingInvoices = false;
      if (!resp && silent) return;
      if (!resp) {
        return this.$swal({
          icon: "error",
          title: "Error",
          text: "No se pudieron consultar las facturas en SAP, intenta nuevamente",
          position: "top-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      this.invoices = resp;
    },
    getLineInvoices(purchase, line) {
      const invoicePurchase = this.invoices.purchases.find(
        (item) => item.sap_code === purchase.sap_code
      );
      const invoiceLine =
        invoicePurchase && invoicePurchase.items.find((item) => item.code === line.code);
      return invoiceLine || { invoices: [], payment_status: "none" };
    },
    async refresh() {
      this.invoices = null;
      if (!this.isPostedToSap) {
        this.bundleStatus = null;
        return;
      }
      this.loading = true;
      this.bundleStatus = await this.getBundleStatus({ bundle_id: this.bundle.id });
      // Entrega has nothing for a posted request (its immediate sync failed): ask cubicador
      // to re-sync its SAP documents once, then reload
      if (
        this.bundleStatus &&
        !this.bundleStatus.transfers.length &&
        !this.bundleStatus.purchases.length &&
        this.resyncedBundleId !== this.bundle.id
      ) {
        this.resyncedBundleId = this.bundle.id;
        this.resyncing = true;
        await this.resyncBundleEntregas({ bundle_id: this.bundle.id });
        this.bundleStatus = await this.getBundleStatus({ bundle_id: this.bundle.id });
        this.resyncing = false;
      }
      this.loading = false;
      // Invoices and payments are always shown, loaded from SAP once the status is in
      if (this.bundleStatus && this.bundleStatus.purchases.length) {
        this.loadInvoices(true);
      }
      this.loadLeadTimes();
    },
    getSliCode,
    getEditStatus(edit) {
      return EDIT_STATUS[edit.status] || { name: edit.status, color: "grey darken-1" };
    },
    getTransferStatusName(transfer) {
      if (transfer.promotion_status === "cancelled") {
        return "Anulada";
      }
      if (transfer.promotion_status === "promoted") {
        return "Traslado generado";
      }
      const picking =
        transfer.picking_from && !transfer.picking_window_open
          ? ` · picking desde el ${formatPickingDayCl(transfer.picking_from)}`
          : "";
      return transfer.fifo_position
        ? `En cola de despacho (posición ${transfer.fifo_position} de ${transfer.fifo_queue_length})${transfer.is_urgent ? " - urgente" : ""}${picking}`
        : "Reservada";
    },
    getReceivedLinesCount(purchase) {
      return purchase.items.filter(
        (line) => line.quantity && line.received_quantity >= line.quantity
      ).length;
    },
  },
  computed: {
    isPostedToSap() {
      return Boolean(
        this.bundle && this.bundle.status === CUBAGE_BUNDLE_STATUS.POSTED_TO_SAP
      );
    },
    ...mapGetters({
      bundleEditOptions: "cubage/bundleEditOptions",
    }),
    // Changes made to the request since it was sent (from bundle_edit_options)
    edits() {
      return (this.bundleEditOptions && this.bundleEditOptions.edits) || [];
    },
    // T-48h: only requests sent with a dispatch date, while still waiting for picking
    editableUntil() {
      if (!this.bundle.desired_dispatch_date || !this.bundleStatus) return null;
      const transfer = this.bundleStatus.transfers[0];
      return transfer &&
        transfer.promotion_status === "pending" &&
        transfer.picking_from &&
        !transfer.picking_window_open
        ? transfer.picking_from
        : null;
    },
    lateLines() {
      if (!this.bundleStatus || !this.bundle.desired_dispatch_date) return [];
      const late = [];
      this.bundleStatus.purchases.forEach((purchase) =>
        purchase.items.forEach((line) => {
          const arrival = this.getArrival(line);
          if (arrival && arrival.late) late.push({ code: line.code, arrival });
        })
      );
      return late;
    },
    requestStatus() {
      return getRequestStatus(this.bundle, this.bundleStatus);
    },
  },
  watch: {
    bundle: {
      immediate: true,
      handler() {
        this.refresh();
      },
    },
  },
};
</script>

<style scoped>
/* ST / SC cards: the status alert around them is dark-themed, so they're forced to the
   light theme (light prop) and drawn as white cards */
.request-documents ::v-deep .v-expansion-panel {
  background-color: #ffffff !important;
  border: 1px solid #e0e0e0;
  border-radius: 8px !important;
  margin-bottom: 8px;
}
.request-documents ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}
.request-documents ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 16px 12px;
}
/* The status alert centres its text; tables align left, numbers right */
.request-table ::v-deep th,
.request-table ::v-deep td {
  text-align: left;
}
.request-table ::v-deep .text-right {
  text-align: right !important;
}
.request-table ::v-deep .text-center {
  text-align: center !important;
}
.request-table ::v-deep th {
  font-size: 12px !important;
  font-weight: 600;
  color: #616161 !important;
  white-space: nowrap;
}
.request-table ::v-deep td {
  font-size: 13px;
}
.arrival-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
</style>
